import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideTransloco } from '@jsverse/transloco';
import { LogLevel, authInterceptor, provideAuth } from 'angular-auth-oidc-client';
import { firstValueFrom } from 'rxjs';

import { routes } from './routes';
import { environment } from '../environments/environment';
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  detectInitialLanguage,
} from './shared/i18n/language.constants';
import { LanguageService } from './shared/i18n/language.service';
import { NamespacedTranslocoLoader } from './shared/i18n/namespaced-transloco.loader';
import { baseUrlInterceptor } from './shared/interceptors/base-url.interceptor';
import { correlationIdInterceptor } from './shared/interceptors/correlation-id.interceptor';
import { errorInterceptor } from './shared/interceptors/error.interceptor';
import { AuthService } from './shared/services/auth.service';

export function getOidcRedirectUrl(): string {
  return typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideHttpClient(
      withInterceptors([
        correlationIdInterceptor,
        baseUrlInterceptor,
        authInterceptor(),
        errorInterceptor,
      ]),
    ),
    provideAppInitializer(() => {
      inject(LanguageService);
    }),
    provideAuth({
      config: {
        configId: 'keycloak',
        authority: environment.auth.authority,
        clientId: environment.auth.clientId,
        redirectUrl: getOidcRedirectUrl(),
        postLogoutRedirectUri: getOidcRedirectUrl(),
        responseType: 'code',
        scope: 'openid profile email offline_access',
        silentRenew: true,
        useRefreshToken: true,
        ignoreNonceAfterRefresh: true,
        triggerAuthorizationResultEvent: true,
        renewTimeBeforeTokenExpiresInSeconds: 10,
        secureRoutes: [environment.apiBaseUrl],
        logLevel: environment.production ? LogLevel.Error : LogLevel.Warn,
      },
    }),
    provideAppInitializer(() => firstValueFrom(inject(AuthService).checkAuth())),
    provideTransloco({
      config: {
        availableLangs: [...SUPPORTED_LANGUAGES],
        defaultLang: detectInitialLanguage(),
        fallbackLang: DEFAULT_LANGUAGE,
        reRenderOnLangChange: true,
        prodMode: environment.production,
        missingHandler: {
          useFallbackTranslation: true,
          allowEmpty: false,
        },
      },
      loader: NamespacedTranslocoLoader,
    }),
  ],
};
